import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../services/api';
import { API, formatError } from '../services/api';
import { motion } from 'framer-motion';
import { Spinner, CheckCircle, Lock, CaretLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

/*
  Change Password — authenticated page. Linked from Profile / settings.
*/
const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const strength = useMemo(() => {
    const pw = newPw;
    if (!pw) return null;
    const issues = [];
    if (pw.length < 8) issues.push('at least 8 characters');
    if (!/[A-Za-z]/.test(pw)) issues.push('a letter');
    if (!/[0-9\W_]/.test(pw)) issues.push('a number or symbol');
    if (pw.trim() !== pw) issues.push('no leading/trailing spaces');
    return issues;
  }, [newPw]);

  const canSubmit = currentPw && newPw && confirm && strength && strength.length === 0 && newPw === confirm && currentPw !== newPw && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirm) return setError('New passwords do not match');
    if (newPw === currentPw) return setError('New password must be different from current');
    if (strength && strength.length > 0) return setError('Password needs: ' + strength.join(', '));

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      });
      setDone(true);
      toast.success('Password updated');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-6 py-12 safe-top" data-testid="change-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white border border-[#235347]/10 rounded-2xl p-8">
            <div className="w-14 h-14 rounded-full bg-[#235347]/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-[#235347]" weight="duotone" />
            </div>
            <h2 className="text-xl font-semibold text-[#0f1a17] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Password updated
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              You're still signed in on this device. Any other sessions have been signed out.
            </p>
            <button
              onClick={() => navigate('/profile')}
              className="inline-block bg-[#235347] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1c4439] transition-colors"
              data-testid="change-back-to-profile"
            >
              Back to profile
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] safe-top">
      <div className="max-w-md mx-auto px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-slate-500 hover:text-[#235347] text-sm mb-6 transition-colors"
          data-testid="change-back"
        >
          <CaretLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-light text-[#0f1a17] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="change-heading">
          Change password
        </h1>
        <p className="text-slate-500 text-sm mb-8">
          Updating your password will sign you out on every other device.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="change-error">{error}</div>
          )}
          <input
            data-testid="change-current-password"
            type="password"
            placeholder="Current password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoComplete="current-password"
          />
          <input
            data-testid="change-new-password"
            type="password"
            placeholder="New password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoComplete="new-password"
          />
          {strength && strength.length > 0 && (
            <div className="text-xs text-slate-500 ml-1" data-testid="change-strength-hint">
              Needs: {strength.join(', ')}
            </div>
          )}
          <input
            data-testid="change-confirm-password"
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoComplete="new-password"
          />
          <button
            data-testid="change-submit"
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-[#235347] text-white rounded-full px-6 py-4 font-medium text-base hover:bg-[#1c4439] transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
          >
            {loading && <Spinner size={20} className="animate-spin" />}
            Update password
          </button>
          <p className="text-xs text-slate-400 flex items-center justify-center gap-1.5 pt-1">
            <Lock size={12} /> Stored hashed · other sessions will be signed out
          </p>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
